import {AntDValueSelector, QueryBuilderAntD} from "@react-querybuilder/antd";
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import {Field, OptionGroup, QueryBuilder, RuleGroupType, RuleType, transformQuery} from "react-querybuilder";
import React, {useContext, useEffect, useState} from "react";
import {formatVulSourceType, VulSourceType} from "../../domain/VulSourceType";
import AttributeDefinition from "../../domain/AttributeDefinition";
import {AiAttributeServiceContext, AttributeDefinitionServiceContext, VulSourceServiceContext} from "../../Contexts";
import QueryOptions from "../../sal-ui/QueryOptions";
import VulSource from "../../domain/VulSource";
import AiAttribute, {AI_ATTRIBUTE_RATING_OPERATORS, AI_ATTRIBUTE_SUMMARY_OPERATORS, AI_ATTRIBUTE_TEXT_OPERATORS, AiAttributeResultType} from "../../domain/AiAttribute";
import {FormInstance} from "antd";
import {QueryBuilderDnD} from "@react-querybuilder/dnd";
import {AttributeValueType} from "../../domain/AttributeValueType";
import CustomValueEditor from "./CustomValueEditor";
import {isValuelessAttributeOperator} from "../../utils/RqbOperatorUtils";

const FILTERABLE_RESULT_TYPES: AiAttributeResultType[] = ["VALUE_RATING", "VALUE_TEXT", "VALUE_TEXTS", "VALUE_SUMMARY"];

const numericOperators = [
    {name: "jsonGe", label: "≥"},
    {name: "jsonEq", label: "="},
    {name: "jsonLe", label: "≤"},
    {name: "jsonPresent", label: "is present"},
    {name: "jsonMissing", label: "is missing"},
];

const textOperators = [
    {name: "jsonEq", label: "="},
    {name: "jsonIcontains", label: "contains"},
    {name: "not(jsonIcontains)", label: "does not contain"},
    {name: "jsonRegex", label: "matches regex"},
    {name: "not(jsonRegex)", label: "does not match regex"},
    {name: "jsonPresent", label: "is present"},
    {name: "jsonMissing", label: "is missing"},
];

interface Props {
    form?: FormInstance;
    initialQuery?: string;
}

type SearchableFieldSelectorProps = React.ComponentProps<typeof AntDValueSelector>;

function SearchableFieldSelector(selectorProps: SearchableFieldSelectorProps) {
    return (
        <AntDValueSelector
            {...selectorProps}
            showSearch={true}
            filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
        />
    );
}

function VulViewQueryBuilder(props: Props) {
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
    const [vulSources, setVulSources] = useState<VulSource[]>();
    const [aiAttributes, setAiAttributes] = useState<AiAttribute[]>([]);
    const [query, setQuery] = useState<RuleGroupType>({
        combinator: 'or',
        rules: [{field: "text", operator: "jsonIcontains", value: undefined}]
    });

    useEffect(() => {
        if (props.initialQuery !== undefined) {
            const initialQuery = JSON.parse(props.initialQuery);
            const transformedQuery = transformQuery(initialQuery, {ruleProcessor: toEditorRule});

            setQuery(transformedQuery);
        }
    }, [props.initialQuery]);

    useEffect(() => {
        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setVulSources(value.data));
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setAttributeDefinitions(value.data));
        aiAttributeService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(value => setAiAttributes(value.data))
            .catch(ignoreAiAttributeLoadFailure);
    }, []);

    const filterableAiAttributes = aiAttributes.filter(aiAttribute =>
        FILTERABLE_RESULT_TYPES.includes(aiAttribute.resultType)
    );
    const systemAiAttributes = filterableAiAttributes.filter(aiAttribute => aiAttribute.owner === "SYSTEM");
    const orgAiAttributes = filterableAiAttributes.filter(aiAttribute => aiAttribute.owner === "ORGANIZATION");

    const orgAiAttributeGroups = orgAiAttributes.reduce<Record<string, AiAttribute[]>>((groups, aiAttribute) => {
        const organizationName = aiAttribute.organization?.name ?? "—";

        (groups[organizationName] ??= []).push(aiAttribute);

        return groups;
    }, {});

    const searchableFieldSelector = {
        fieldSelector: SearchableFieldSelector
    };

    function aiAttributeToField(aiAttribute: AiAttribute): Field {
        if (aiAttribute.resultType === "VALUE_TEXT" || aiAttribute.resultType === "VALUE_TEXTS") {
            return {
                name: `aiAttributes-${aiAttribute.id}`,
                label: aiAttribute.name!,
                operators: AI_ATTRIBUTE_TEXT_OPERATORS,
                aiAttributeResultType: aiAttribute.resultType,
                ...(aiAttribute.allowedValues?.length > 0 ? {
                    values: aiAttribute.allowedValues.map(value => ({name: value, label: value}))
                } : {})
            };
        }

        if (aiAttribute.resultType === "VALUE_SUMMARY") {
            return {
                name: `aiAttributes-${aiAttribute.id}`,
                label: aiAttribute.name!,
                operators: AI_ATTRIBUTE_SUMMARY_OPERATORS,
                aiAttributeResultType: "VALUE_SUMMARY",
            };
        }

        return {
            name: `aiAttributes-${aiAttribute.id}`,
            label: aiAttribute.name!,
            operators: AI_ATTRIBUTE_RATING_OPERATORS,
            aiAttributeResultType: "VALUE_RATING",
        };
    }

    const fields: OptionGroup<Field>[] = [
        {
            label: "Source",
            options: [
                {
                    name: 'source.type',
                    label: 'Source type',
                    operators: [
                        {name: 'in', label: 'in'},
                        {name: 'notIn', label: 'not in'}
                    ],
                    valueEditorType: 'multiselect',
                    values: Object.values(VulSourceType).map(value => ({
                        name: value,
                        label: formatVulSourceType(value)
                    })),
                    validator: rule => rule.value !== undefined && rule.value !== null && rule.value.length !== 0
                },
                {
                    name: 'source.id',
                    label: 'Source',
                    operators: [
                        {name: 'in', label: 'in'},
                        {name: 'notIn', label: 'not in'}
                    ],
                    valueEditorType: 'multiselect',
                    values: vulSources,
                    validator: rule => rule.value !== undefined && rule.value !== null && rule.value.length !== 0
                }
            ]
        },
        {
            label: "General",
            options: [
                {
                    name: 'text',
                    label: 'Text',
                    operators: [
                        {name: 'jsonIcontains', label: 'contains'},
                        {name: 'not(jsonIcontains)', label: 'does not contain'},
                        {name: 'jsonRegex', label: 'matches regex'},
                        {name: 'not(jsonRegex)', label: 'does not match regex'}
                    ]
                }
            ]
        },
        {
            label: "Attributes",
            options: attributeDefinitions.map(definition => ({
                name: `attributes-${definition.name}`,
                label: definition.name!,
                operators: (definition.valueType === AttributeValueType.NUMBER) ? numericOperators : textOperators
            }))
        },
        ...(systemAiAttributes.length > 0 ? [{
            label: "System AI attributes",
            options: systemAiAttributes.map(aiAttributeToField)
        }] : []),
        ...Object.entries(orgAiAttributeGroups).map(([orgName, organizationAiAttributes]) => ({
            label: `AI attributes: ${orgName}`,
            options: organizationAiAttributes.map(aiAttributeToField)
        }))
    ];

    const commonControlElements = {
        ...searchableFieldSelector,
        valueEditor: CustomValueEditor
    };

    return (
        <QueryBuilderDnD dnd={{...ReactDnD, ...ReactDndHtml5Backend}}>
            <QueryBuilderAntD>
                <QueryBuilder
                    fields={fields}
                    query={query}
                    controlElements={commonControlElements}
                    onQueryChange={onQueryChange}
                    listsAsArrays={true}
                    getDefaultValue={() => undefined}
                />
            </QueryBuilderAntD>
        </QueryBuilderDnD>
    );

    function onQueryChange(q: RuleGroupType) {
        setQuery(q);

        const transformedQuery = transformQuery(q, {ruleProcessor: toPersistedRule});

        if (props.form) {
            props.form.setFieldValue("query", JSON.stringify(transformedQuery));
        }
    }

    function toEditorRule(rule: RuleType): RuleType {
        if (rule.field === "attributes") {
            const attributeName = Array.isArray(rule.value) ? rule.value[0] : rule.value;
            const attributeValue = Array.isArray(rule.value) && !isValuelessAttributeOperator(rule.operator)
                ? rule.value[1]
                : undefined;

            return {
                ...rule,
                field: `attributes-${attributeName}`,
                value: attributeValue
            };
        }

        if (rule.field.startsWith("aiAttributes.")) {
            const aiAttributeId = rule.field.replace("aiAttributes.", "");

            return {
                ...rule,
                field: `aiAttributes-${aiAttributeId}`
            };
        }

        return rule;
    }

    function toPersistedRule(rule: RuleType): RuleType {
        if (rule.field.startsWith("attributes-")) {
            const attributeName = rule.field.substring("attributes-".length);

            return {
                ...rule,
                field: "attributes",
                value: isValuelessAttributeOperator(rule.operator) ? attributeName : [attributeName, rule.value]
            };
        }

        if (rule.field.startsWith("aiAttributes-")) {
            const aiAttributeIdentifier = rule.field.replace("aiAttributes-", "");

            return {
                ...rule,
                field: `aiAttributes.${aiAttributeIdentifier}`
            };
        }

        return rule;
    }

    function ignoreAiAttributeLoadFailure() {
        // AI attributes are optional here; keep the builder usable without them.
    }
}

export default VulViewQueryBuilder;
